// src/pages/outsourcing_management/requisitions/RequisitionAuditTrail.tsx
//
// EE-207 Phase 4 (#16) - renders the append-only audit trail
// (RequisitionAuditVM rows) as a timeline: action, from -> to status,
// actor, comment, timestamp. Only populated on the single-GET detail response
// (empty on list rows) - see the casing note in RequisitionTypes.ts -
// so this must only ever be fed audit_trail from useRequisition(id),
// never from the list query.
//
// The rendering itself lives in components/Dashboard/AuditTrail - the inquiry
// has the same trail, column for column, and only the action codes and the
// status labels differ. This file is now those differences.
import { FunctionComponent } from "react";
import AuditTrail from "components/Dashboard/AuditTrail";
import { RequisitionAuditType } from "types/RequisitionTypes";
import { requisitionStatusLabel } from "../constants/requisitionStatusColors";

type PropsType = {
  auditTrail: RequisitionAuditType[];
};

const ACTION_DOT_COLOR: Record<string, string> = {
  CREATE: "#868686",
  UPDATE: "#868686",
  SUBMIT: "#007474",
  REJECT: "var(--mainYellow)",
  CLOSE: "#4b4b4b",
  CANCEL: "#e12c4f",
  // One line cancelled; the requisition goes on.
  CANCEL_LINE: "#e12c4f",
  UPDATE_EDITABLE_FIELDS: "#868686",
  // CREATE_ENQUIRY is what submitting records since EE-253. APPROVE, ADVANCE
  // and CONVERT are retired actions - approval is an Inquiry transition now,
  // and it reaches the trail as SYNC_FROM_INQUIRY - kept here so historic rows
  // keep their colour.
  APPROVE: "#008b80",
  ADVANCE: "#007474",
  CREATE_ENQUIRY: "#085e5b",
  CONVERT: "#085e5b",
  // Status changes the linked Inquiry made on this requisition's behalf
  // (#8/#9) - same colour family, since both come from the Inquiry.
  SYNC_FROM_INQUIRY: "#085e5b",
  // The acknowledgement of one of those changes (EE-253) - a reading, not a
  // move, so it stays quiet.
  ACK_STATUS_UPDATE: "#868686",
};

// The action codes are the backend's (REQUISITION_AUDIT.action) and two of
// them spell the record "Inquiry". The rest of the UI calls it an Inquiry, so
// they are relabelled here rather than renamed there - the codes are stored on
// every row written since EE-253.
const ACTION_LABELS: Record<string, string> = {
  CREATE_ENQUIRY: "Create Inquiry",
  CREATE_ENQUIRY_FAILED: "Create Inquiry Failed",
  // Would otherwise fall through to "Ack Status Update".
  ACK_STATUS_UPDATE: "Status Update Acknowledged",
};

const RequisitionAuditTrail: FunctionComponent<PropsType> = ({ auditTrail }) => (
  <AuditTrail
    entries={auditTrail}
    dotColors={ACTION_DOT_COLOR}
    actionLabels={ACTION_LABELS}
    statusLabel={requisitionStatusLabel}
  />
);

export default RequisitionAuditTrail;
